import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { User, Users, Phone, Mail, GraduationCap, Heart, HelpCircle, FileText, CheckCircle, Plus, Trash2, CheckCircle2, Loader2, Download } from "lucide-react";
import { apiCall } from "../lib/api";

export const EnrollmentForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [formData, setFormData] = useState({
    // Section 1: Personal Information
    surname: "",
    firstName: "",
    middleName: "",
    gender: "",
    dateOfBirth: "",
    placeOfBirth: "",
    nationality: "",
    religion: "",
    
    // Section 2: Previous Schools
    previousSchools: [{ name: "", dateFrom: "", dateTo: "", classAtLeaving: "", reasonForLeaving: "" }],
    
    // Section 3: Father's Contact
    fatherName: "",
    fatherPostalAddress: "",
    fatherResidentialAddress: "",
    fatherHomeTel: "",
    fatherWorkTel: "",
    fatherMobile: "",
    fatherOccupation: "",
    fatherEmail: "",
    
    // Section 3: Mother's Contact
    motherName: "",
    motherPostalAddress: "",
    motherResidentialAddress: "",
    motherHomeTel: "",
    motherWorkTel: "",
    motherMobile: "",
    motherOccupation: "",
    motherEmail: "",
    
    // Section 4: Guardian
    guardianName: "",
    guardianPostalAddress: "",
    guardianResidentialAddress: "",
    guardianHomeTel: "",
    guardianWorkTel: "",
    guardianMobile: "",
    guardianRelationship: "",
    guardianOccupation: "",
    guardianEmail: "",
    
    // Section 5: Emergency Contact
    emergencyName: "",
    emergencyTel: "",
    
    // Section 6: Correspondence Address
    correspondenceAddress: "",
    
    // Section 7: Child Health
    disabilities: [],
    disabilityDetails: "",
    
    // Section 8: Learning Difficulties
    learningDifficulties: [],
    
    // Section 9: How Did You Hear About Us
    referralSources: [],
    referralTeacherName: "",
    referralOther: "",
    
    // Section 10: Declaration
    declarantName: "",
    securityWord: "",
    consentToContact: false
  });

  const totalSteps = 6;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter(item => item !== value)
    }));
  };

  const addPreviousSchool = () => {
    setFormData(prev => ({
      ...prev,
      previousSchools: [...prev.previousSchools, { name: "", dateFrom: "", dateTo: "", classAtLeaving: "", reasonForLeaving: "" }]
    }));
  };

  const removePreviousSchool = (index) => {
    setFormData(prev => ({
      ...prev,
      previousSchools: prev.previousSchools.filter((_, i) => i !== index)
    }));
  };

  const updatePreviousSchool = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      previousSchools: prev.previousSchools.map((school, i) => 
        i === index ? { ...school, [field]: value } : school
      )
    }));
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.surname || !formData.firstName || !formData.gender || !formData.dateOfBirth) {
      toast.error("Please fill in all required personal information fields.");
      return;
    }
    if (!formData.fatherMobile || !formData.fatherEmail || !formData.motherMobile || !formData.motherEmail) {
      toast.error("Please fill in all required parent contact information.");
      return;
    }
    if (!formData.emergencyName || !formData.emergencyTel) {
      toast.error("Please fill in emergency contact information.");
      return;
    }
    if (!formData.declarantName || !formData.securityWord) {
      toast.error("Please fill in the declaration section.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiCall("POST", "/enrollment/apply", formData);
      setApplicationId(response.application_id);
      setIsSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(error.message || "Failed to submit application. Please try again.");
    }
    setIsSubmitting(false);
  };

  // Success Screen after submission
  const renderSuccessScreen = () => (
    <div className="text-center py-12" data-testid="success-screen">
      <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <CheckCircle2 className="w-16 h-16 text-green-600" />
      </div>
      <h2 className="text-3xl font-bold text-[#002147] mb-4">Application Submitted!</h2>
      <p className="text-gray-600 mb-2">Thank you for applying to Young Christian Preparatory School.</p>
      <p className="text-gray-600 mb-6">Your application ID is:</p>
      <div className="bg-gray-100 rounded-lg p-4 max-w-md mx-auto mb-8">
        <code className="text-lg font-mono text-[#FF6B00]">{applicationId}</code>
      </div>
      <p className="text-gray-600 mb-8">
        We will review your application and contact you soon via email or phone.
        <br />Please keep your application ID for reference.
      </p>
      <div className="flex justify-center gap-4">
        <Button 
          variant="outline"
          onClick={() => window.location.href = "/"}
          data-testid="go-home-btn"
        >
          Go to Homepage
        </Button>
        <Button 
          className="bg-[#FF6B00] hover:bg-[#E65C00]"
          onClick={() => {
            setIsSubmitted(false);
            setCurrentStep(1);
            setFormData({
              surname: "", firstName: "", middleName: "", gender: "", dateOfBirth: "",
              placeOfBirth: "", nationality: "", religion: "",
              previousSchools: [{ name: "", dateFrom: "", dateTo: "", classAtLeaving: "", reasonForLeaving: "" }],
              fatherName: "", fatherPostalAddress: "", fatherResidentialAddress: "",
              fatherHomeTel: "", fatherWorkTel: "", fatherMobile: "", fatherOccupation: "", fatherEmail: "",
              motherName: "", motherPostalAddress: "", motherResidentialAddress: "",
              motherHomeTel: "", motherWorkTel: "", motherMobile: "", motherOccupation: "", motherEmail: "",
              guardianName: "", guardianPostalAddress: "", guardianResidentialAddress: "",
              guardianHomeTel: "", guardianWorkTel: "", guardianMobile: "", guardianRelationship: "",
              guardianOccupation: "", guardianEmail: "",
              emergencyName: "", emergencyTel: "", correspondenceAddress: "",
              disabilities: [], disabilityDetails: "", learningDifficulties: [],
              referralSources: [], referralTeacherName: "", referralOther: "",
              declarantName: "", securityWord: "", consentToContact: false
            });
          }}
          data-testid="submit-another-btn"
        >
          Submit Another Application
        </Button>
      </div>
    </div>
  );

  const renderStepIndicator = () => (
    <div className="flex justify-center mb-8">
      <div className="flex items-center space-x-2">
        {[1, 2, 3, 4, 5, 6].map((step) => (
          <React.Fragment key={step}>
            <button
              type="button"
              onClick={() => setCurrentStep(step)}
              className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all cursor-pointer hover:scale-110 ${
                currentStep === step 
                  ? "bg-[#FF6B00] text-white ring-2 ring-[#FF6B00] ring-offset-2" 
                  : currentStep > step 
                    ? "bg-green-500 text-white hover:bg-green-600"
                    : "bg-gray-200 text-gray-500 hover:bg-gray-300"
              }`}
              data-testid={`step-btn-${step}`}
            >
              {currentStep > step ? <CheckCircle size={20} /> : step}
            </button>
            {step < 6 && <div className={`w-8 h-1 ${currentStep > step ? "bg-green-500" : "bg-gray-200"}`} />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6" data-testid="step-1-personal-info">
      <div className="flex items-center gap-3 mb-6">
        <User className="w-6 h-6 text-[#FF6B00]" />
        <h3 className="text-xl font-bold text-[#002147]">Section 1: Personal Information</h3>
      </div>
      
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="surname">Surname *</Label>
          <Input 
            id="surname" 
            value={formData.surname} 
            onChange={(e) => handleInputChange("surname", e.target.value)}
            placeholder="Enter surname"
            required
          />
        </div>
        <div>
          <Label htmlFor="firstName">First Name *</Label>
          <Input 
            id="firstName" 
            value={formData.firstName} 
            onChange={(e) => handleInputChange("firstName", e.target.value)}
            placeholder="Enter first name"
            required
          />
        </div>
        <div>
          <Label htmlFor="middleName">Middle Name</Label>
          <Input 
            id="middleName" 
            value={formData.middleName} 
            onChange={(e) => handleInputChange("middleName", e.target.value)}
            placeholder="Enter middle name"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>Gender *</Label>
          <Select value={formData.gender} onValueChange={(value) => handleInputChange("gender", value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select gender" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="dateOfBirth">Date of Birth *</Label>
          <Input 
            id="dateOfBirth" 
            type="date"
            value={formData.dateOfBirth} 
            onChange={(e) => handleInputChange("dateOfBirth", e.target.value)}
            required
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="placeOfBirth">Place of Birth *</Label>
          <Input 
            id="placeOfBirth" 
            value={formData.placeOfBirth} 
            onChange={(e) => handleInputChange("placeOfBirth", e.target.value)}
            placeholder="Enter place of birth"
            required
          />
        </div>
        <div>
          <Label htmlFor="nationality">Nationality *</Label>
          <Input 
            id="nationality" 
            value={formData.nationality} 
            onChange={(e) => handleInputChange("nationality", e.target.value)}
            placeholder="Enter nationality"
            required
          />
        </div>
      </div>

      <div>
        <Label htmlFor="religion">Religion</Label>
        <Input 
          id="religion" 
          value={formData.religion} 
          onChange={(e) => handleInputChange("religion", e.target.value)}
          placeholder="Enter religion"
        />
      </div>

      {/* Section 2: Previous Schools */}
      <div className="pt-6 border-t">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <GraduationCap className="w-6 h-6 text-[#FF6B00]" />
            <h3 className="text-xl font-bold text-[#002147]">Section 2: Previous School(s) Attended</h3>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addPreviousSchool}>
            <Plus size={16} className="mr-1" /> Add School
          </Button>
        </div>

        {formData.previousSchools.map((school, index) => (
          <div key={index} className="p-4 border rounded-lg mb-4 bg-gray-50">
            <div className="flex justify-between items-center mb-3">
              <span className="font-medium text-sm text-gray-600">School {index + 1}</span>
              {index > 0 && (
                <Button type="button" variant="ghost" size="sm" onClick={() => removePreviousSchool(index)}>
                  <Trash2 size={16} className="text-red-500" />
                </Button>
              )}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Name and Address of School</Label>
                <Input 
                  value={school.name} 
                  onChange={(e) => updatePreviousSchool(index, "name", e.target.value)}
                  placeholder="School name and address"
                />
              </div>
              <div>
                <Label>Date From</Label>
                <Input 
                  type="date"
                  value={school.dateFrom} 
                  onChange={(e) => updatePreviousSchool(index, "dateFrom", e.target.value)}
                />
              </div>
              <div>
                <Label>Date To</Label>
                <Input 
                  type="date"
                  value={school.dateTo} 
                  onChange={(e) => updatePreviousSchool(index, "dateTo", e.target.value)}
                />
              </div>
              <div>
                <Label>Class/Form at Time of Leaving</Label>
                <Input 
                  value={school.classAtLeaving} 
                  onChange={(e) => updatePreviousSchool(index, "classAtLeaving", e.target.value)}
                  placeholder="e.g., Class 6"
                />
              </div>
              <div>
                <Label>Reason for Leaving</Label>
                <Input 
                  value={school.reasonForLeaving} 
                  onChange={(e) => updatePreviousSchool(index, "reasonForLeaving", e.target.value)}
                  placeholder="e.g., Relocation"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6" data-testid="step-2-father-info">
      <div className="flex items-center gap-3 mb-6">
        <Users className="w-6 h-6 text-[#FF6B00]" />
        <h3 className="text-xl font-bold text-[#002147]">Section 3: Father's Contact Information</h3>
      </div>

      <div>
        <Label htmlFor="fatherName">Father's Name *</Label>
        <Input 
          id="fatherName" 
          value={formData.fatherName} 
          onChange={(e) => handleInputChange("fatherName", e.target.value)}
          placeholder="Enter father's full name"
          required
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="fatherPostalAddress">Postal Address</Label>
          <Input 
            id="fatherPostalAddress" 
            value={formData.fatherPostalAddress} 
            onChange={(e) => handleInputChange("fatherPostalAddress", e.target.value)}
            placeholder="P.O. Box..."
          />
        </div>
        <div>
          <Label htmlFor="fatherResidentialAddress">Residential Address *</Label>
          <Input 
            id="fatherResidentialAddress" 
            value={formData.fatherResidentialAddress} 
            onChange={(e) => handleInputChange("fatherResidentialAddress", e.target.value)}
            placeholder="House/Street address"
            required
          />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="fatherHomeTel">Home Tel</Label>
          <Input 
            id="fatherHomeTel" 
            value={formData.fatherHomeTel} 
            onChange={(e) => handleInputChange("fatherHomeTel", e.target.value)}
            placeholder="Home telephone"
          />
        </div>
        <div>
          <Label htmlFor="fatherWorkTel">Work Tel</Label>
          <Input 
            id="fatherWorkTel" 
            value={formData.fatherWorkTel} 
            onChange={(e) => handleInputChange("fatherWorkTel", e.target.value)}
            placeholder="Work telephone"
          />
        </div>
        <div>
          <Label htmlFor="fatherMobile">Mobile *</Label>
          <Input 
            id="fatherMobile" 
            value={formData.fatherMobile} 
            onChange={(e) => handleInputChange("fatherMobile", e.target.value)}
            placeholder="Mobile number"
            required
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="fatherOccupation">Occupation</Label>
          <Input 
            id="fatherOccupation" 
            value={formData.fatherOccupation} 
            onChange={(e) => handleInputChange("fatherOccupation", e.target.value)}
            placeholder="e.g., Engineer"
          />
        </div>
        <div>
          <Label htmlFor="fatherEmail">Email Address *</Label>
          <Input 
            id="fatherEmail" 
            type="email"
            value={formData.fatherEmail} 
            onChange={(e) => handleInputChange("fatherEmail", e.target.value)}
            placeholder="father@email.com"
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6" data-testid="step-3-mother-info">
      <div className="flex items-center gap-3 mb-6">
        <Users className="w-6 h-6 text-[#FF6B00]" />
        <h3 className="text-xl font-bold text-[#002147]">Section 3: Mother's Contact Information</h3>
      </div>

      <div>
        <Label htmlFor="motherName">Mother's Name *</Label>
        <Input 
          id="motherName" 
          value={formData.motherName} 
          onChange={(e) => handleInputChange("motherName", e.target.value)}
          placeholder="Enter mother's full name"
          required
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="motherPostalAddress">Postal Address</Label>
          <Input 
            id="motherPostalAddress" 
            value={formData.motherPostalAddress} 
            onChange={(e) => handleInputChange("motherPostalAddress", e.target.value)}
            placeholder="P.O. Box..."
          />
        </div>
        <div>
          <Label htmlFor="motherResidentialAddress">Residential Address *</Label>
          <Input 
            id="motherResidentialAddress" 
            value={formData.motherResidentialAddress} 
            onChange={(e) => handleInputChange("motherResidentialAddress", e.target.value)}
            placeholder="House/Street address"
            required
          />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="motherHomeTel">Home Tel</Label>
          <Input 
            id="motherHomeTel" 
            value={formData.motherHomeTel} 
            onChange={(e) => handleInputChange("motherHomeTel", e.target.value)}
            placeholder="Home telephone"
          />
        </div>
        <div>
          <Label htmlFor="motherWorkTel">Work Tel</Label>
          <Input 
            id="motherWorkTel" 
            value={formData.motherWorkTel} 
            onChange={(e) => handleInputChange("motherWorkTel", e.target.value)}
            placeholder="Work telephone"
          />
        </div>
        <div>
          <Label htmlFor="motherMobile">Mobile *</Label>
          <Input 
            id="motherMobile" 
            value={formData.motherMobile} 
            onChange={(e) => handleInputChange("motherMobile", e.target.value)}
            placeholder="Mobile number"
            required
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="motherOccupation">Occupation</Label>
          <Input 
            id="motherOccupation" 
            value={formData.motherOccupation} 
            onChange={(e) => handleInputChange("motherOccupation", e.target.value)}
            placeholder="e.g., Teacher"
          />
        </div>
        <div>
          <Label htmlFor="motherEmail">Email Address *</Label>
          <Input 
            id="motherEmail" 
            type="email"
            value={formData.motherEmail} 
            onChange={(e) => handleInputChange("motherEmail", e.target.value)}
            placeholder="mother@email.com"
            required
          />
        </div>
      </div>

      {/* Guardian Section */}
      <div className="pt-6 border-t">
        <div className="flex items-center gap-3 mb-6">
          <Users className="w-6 h-6 text-[#FF6B00]" />
          <h3 className="text-xl font-bold text-[#002147]">Section 4: Guardian (If applicable)</h3>
        </div>

        <div>
          <Label htmlFor="guardianName">Guardian's Name</Label>
          <Input 
            id="guardianName" 
            value={formData.guardianName} 
            onChange={(e) => handleInputChange("guardianName", e.target.value)}
            placeholder="Enter guardian's full name"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div>
            <Label htmlFor="guardianPostalAddress">Postal Address</Label>
            <Input 
              id="guardianPostalAddress" 
              value={formData.guardianPostalAddress} 
              onChange={(e) => handleInputChange("guardianPostalAddress", e.target.value)}
              placeholder="P.O. Box..."
            />
          </div>
          <div>
            <Label htmlFor="guardianResidentialAddress">Residential Address</Label>
            <Input 
              id="guardianResidentialAddress" 
              value={formData.guardianResidentialAddress} 
              onChange={(e) => handleInputChange("guardianResidentialAddress", e.target.value)}
              placeholder="House/Street address"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <div>
            <Label htmlFor="guardianHomeTel">Home Tel</Label>
            <Input 
              id="guardianHomeTel" 
              value={formData.guardianHomeTel} 
              onChange={(e) => handleInputChange("guardianHomeTel", e.target.value)}
              placeholder="Home telephone"
            />
          </div>
          <div>
            <Label htmlFor="guardianWorkTel">Work Tel</Label>
            <Input 
              id="guardianWorkTel" 
              value={formData.guardianWorkTel} 
              onChange={(e) => handleInputChange("guardianWorkTel", e.target.value)}
              placeholder="Work telephone"
            />
          </div>
          <div>
            <Label htmlFor="guardianMobile">Mobile</Label>
            <Input 
              id="guardianMobile" 
              value={formData.guardianMobile} 
              onChange={(e) => handleInputChange("guardianMobile", e.target.value)}
              placeholder="Mobile number"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <div>
            <Label htmlFor="guardianRelationship">Relationship to Child</Label>
            <Input 
              id="guardianRelationship" 
              value={formData.guardianRelationship} 
              onChange={(e) => handleInputChange("guardianRelationship", e.target.value)}
              placeholder="e.g., Uncle, Aunt"
            />
          </div>
          <div>
            <Label htmlFor="guardianOccupation">Occupation</Label>
            <Input 
              id="guardianOccupation" 
              value={formData.guardianOccupation} 
              onChange={(e) => handleInputChange("guardianOccupation", e.target.value)}
              placeholder="e.g., Doctor"
            />
          </div>
          <div>
            <Label htmlFor="guardianEmail">Email Address</Label>
            <Input 
              id="guardianEmail" 
              type="email"
              value={formData.guardianEmail} 
              onChange={(e) => handleInputChange("guardianEmail", e.target.value)}
              placeholder="guardian@email.com"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6" data-testid="step-4-emergency-contact">
      {/* Emergency Contact */}
      <div className="flex items-center gap-3 mb-6">
        <Phone className="w-6 h-6 text-[#FF6B00]" />
        <h3 className="text-xl font-bold text-[#002147]">Section 5: Emergency Contact</h3>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="emergencyName">Emergency Contact Name *</Label>
          <Input 
            id="emergencyName" 
            value={formData.emergencyName} 
            onChange={(e) => handleInputChange("emergencyName", e.target.value)}
            placeholder="Enter emergency contact name"
            required
          />
        </div>
        <div>
          <Label htmlFor="emergencyTel">Emergency Contact Tel *</Label>
          <Input 
            id="emergencyTel" 
            value={formData.emergencyTel} 
            onChange={(e) => handleInputChange("emergencyTel", e.target.value)}
            placeholder="Emergency telephone number"
            required
          />
        </div>
      </div>

      {/* Correspondence Address */}
      <div className="pt-6 border-t">
        <div className="flex items-center gap-3 mb-6">
          <Mail className="w-6 h-6 text-[#FF6B00]" />
          <h3 className="text-xl font-bold text-[#002147]">Section 6: Correspondence Address</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">Please select whose address should be used for correspondence:</p>
        
        <div className="space-y-3">
          {["father", "mother", "guardian"].map((option) => (
            <div key={option} className="flex items-center space-x-2">
              <input
                type="radio"
                id={`correspondence-${option}`}
                name="correspondenceAddress"
                value={option}
                checked={formData.correspondenceAddress === option}
                onChange={(e) => handleInputChange("correspondenceAddress", e.target.value)}
                className="w-4 h-4 text-[#FF6B00]"
              />
              <Label htmlFor={`correspondence-${option}`} className="capitalize">{option}'s Address</Label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-6" data-testid="step-5-health-info">
      {/* Child Health */}
      <div className="flex items-center gap-3 mb-6">
        <Heart className="w-6 h-6 text-[#FF6B00]" />
        <h3 className="text-xl font-bold text-[#002147]">Section 7: Child Health</h3>
      </div>
      <p className="text-sm text-gray-600 mb-4">Please tick any that apply:</p>

      <div className="grid md:grid-cols-2 gap-3">
        {[
          "Visual Impairment",
          "Hearing Impairment",
          "Epilepsy",
          "Emotional",
          "Profound/Complex Disability",
          "Multiple Disability",
          "Other Physical Disability",
          "Wheelchair User",
          "Mentally Ill",
          "No Disability"
        ].map((disability) => (
          <div key={disability} className="flex items-center space-x-2">
            <Checkbox
              id={`disability-${disability}`}
              checked={formData.disabilities.includes(disability)}
              onCheckedChange={(checked) => handleCheckboxChange("disabilities", disability, checked)}
            />
            <Label htmlFor={`disability-${disability}`}>{disability}</Label>
          </div>
        ))}
      </div>

      <div className="mt-4">
        <Label htmlFor="disabilityDetails">If any disability is indicated, please state details:</Label>
        <Textarea 
          id="disabilityDetails" 
          value={formData.disabilityDetails} 
          onChange={(e) => handleInputChange("disabilityDetails", e.target.value)}
          placeholder="Provide additional details about any disability..."
          rows={3}
        />
      </div>

      {/* Learning Difficulties */}
      <div className="pt-6 border-t">
        <div className="flex items-center gap-3 mb-6">
          <GraduationCap className="w-6 h-6 text-[#FF6B00]" />
          <h3 className="text-xl font-bold text-[#002147]">Section 8: Learning Difficulties</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">Please tick any that apply:</p>

        <div className="space-y-3">
          {[
            "Moderate Learning Difficulties",
            "Multiple Learning Difficulties",
            "Dyslexia"
          ].map((difficulty) => (
            <div key={difficulty} className="flex items-center space-x-2">
              <Checkbox
                id={`difficulty-${difficulty}`}
                checked={formData.learningDifficulties.includes(difficulty)}
                onCheckedChange={(checked) => handleCheckboxChange("learningDifficulties", difficulty, checked)}
              />
              <Label htmlFor={`difficulty-${difficulty}`}>{difficulty}</Label>
            </div>
          ))}
        </div>
      </div>

      {/* How Did You Hear About Us */}
      <div className="pt-6 border-t">
        <div className="flex items-center gap-3 mb-6">
          <HelpCircle className="w-6 h-6 text-[#FF6B00]" />
          <h3 className="text-xl font-bold text-[#002147]">Section 9: How Did You Hear About Us?</h3>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          {[
            "Prospectus",
            "Website",
            "Word of Mouth",
            "Radio",
            "Newspapers",
            "Direct Enquiry to the School"
          ].map((source) => (
            <div key={source} className="flex items-center space-x-2">
              <Checkbox
                id={`source-${source}`}
                checked={formData.referralSources.includes(source)}
                onCheckedChange={(checked) => handleCheckboxChange("referralSources", source, checked)}
              />
              <Label htmlFor={`source-${source}`}>{source}</Label>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Checkbox
                id="source-teacher"
                checked={formData.referralSources.includes("Teacher")}
                onCheckedChange={(checked) => handleCheckboxChange("referralSources", "Teacher", checked)}
              />
              <Label htmlFor="source-teacher">Teacher</Label>
            </div>
            {formData.referralSources.includes("Teacher") && (
              <Input 
                value={formData.referralTeacherName} 
                onChange={(e) => handleInputChange("referralTeacherName", e.target.value)}
                placeholder="Please state teacher's name"
              />
            )}
          </div>
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Checkbox
                id="source-other"
                checked={formData.referralSources.includes("Other")}
                onCheckedChange={(checked) => handleCheckboxChange("referralSources", "Other", checked)}
              />
              <Label htmlFor="source-other">Other</Label>
            </div>
            {formData.referralSources.includes("Other") && (
              <Input 
                value={formData.referralOther} 
                onChange={(e) => handleInputChange("referralOther", e.target.value)}
                placeholder="Please specify"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-6" data-testid="step-6-declaration">
      <div className="flex items-center gap-3 mb-6">
        <FileText className="w-6 h-6 text-[#FF6B00]" />
        <h3 className="text-xl font-bold text-[#002147]">Section 10: Declaration</h3>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg border">
        <p className="text-sm text-gray-700 mb-4">
          I hereby declare that all information provided in this application form is true and accurate to the best of my knowledge. 
          I understand that providing false information may result in the rejection of this application or dismissal of the student.
        </p>
        <p className="text-sm text-gray-700">
          I agree to abide by the rules and regulations of Young Christian Preparatory School/ JHS and ensure my child/ward does the same.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="declarantName">Parent/Guardian Name (Mr./Mrs./Miss) *</Label>
          <Input 
            id="declarantName" 
            value={formData.declarantName} 
            onChange={(e) => handleInputChange("declarantName", e.target.value)}
            placeholder="Enter your full name"
            required
          />
        </div>
        <div>
          <Label htmlFor="securityWord">Security Word *</Label>
          <Input 
            id="securityWord" 
            value={formData.securityWord} 
            onChange={(e) => handleInputChange("securityWord", e.target.value)}
            placeholder="Create a security word"
            required
          />
          <p className="text-xs text-gray-500 mt-1">This word will be used to verify your identity</p>
        </div>
      </div>

      <div className="flex items-start space-x-2 mt-4">
        <Checkbox
          id="consentToContact"
          checked={formData.consentToContact}
          onCheckedChange={(checked) => handleInputChange("consentToContact", checked)}
        />
        <div>
          <Label htmlFor="consentToContact">I consent to being contacted for surveys and research purposes</Label>
          <p className="text-xs text-gray-500">(Optional)</p>
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch(currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      default: return renderStep1();
    }
  };

  const [showFeeOptions, setShowFeeOptions] = useState(false);

  return (
    <div className="py-8 md:py-12 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <Card className="bg-white shadow-xl">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg py-8 pb-16 relative">
            {/* Centered Content */}
            <div className="text-center">
              {/* School Logo */}
              <div className="flex justify-center mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_schoolify-17/artifacts/jpw8rjex_image.png" 
                  alt="Young Christian Preparatory School Logo" 
                  className="w-24 h-24 md:w-28 md:h-28 object-contain bg-white rounded-full p-2"
                  data-testid="school-logo"
                />
              </div>
              
              {/* Title */}
              <CardTitle className="text-3xl md:text-4xl font-bold mb-2">
                Student Admission Form
              </CardTitle>
              
              {/* School Name */}
              <p className="text-xl md:text-2xl font-semibold text-gray-200 mb-4">Young Christian Prep & Junior High School</p>
              
              {/* Contact Info - Centered */}
              <div className="text-sm text-gray-300 space-y-1">
                <p className="font-semibold text-gray-200 mt-2">Main Campus - Takoradi</p>
                <p>P.O. Box 682, Takoradi</p>
                <p>Mobile: (+055-2184037) | WhatsApp: (+055-2184037) | Email: main@ycpschool.com</p>
                <p className="font-semibold text-gray-200 mt-2">Annex Campus - Lagos Town</p>
                <p>P.O. Box 682, Lagos Town</p>
                <p>Mobile: (+055-2184037) | WhatsApp: (+055-2184037) | Email: annex@ycpschool.com</p>
                <p className="mt-1">www.ycpschool.com</p>
              </div>
            </div>
            
            {/* Pay Application Fee Button - Bottom Right */}
            <div className="absolute bottom-4 right-4 flex items-center gap-3">
              {/* Download PDF Button - 3D Style */}
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/enrollment/download-form`}
                download
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg cursor-pointer transition-all duration-200 hover:brightness-110 active:translate-y-[2px] active:shadow-none"
                style={{
                  background: 'linear-gradient(145deg, #2563eb, #1d4ed8)',
                  boxShadow: '0 4px 0 #1e3a8a, 0 6px 10px rgba(0,0,0,0.3)',
                  transform: 'translateY(-2px)',
                }}
                onMouseDown={(e) => { e.currentTarget.style.transform = 'translateY(0px)'; e.currentTarget.style.boxShadow = '0 1px 0 #1e3a8a, 0 2px 4px rgba(0,0,0,0.2)'; }}
                onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 0 #1e3a8a, 0 6px 10px rgba(0,0,0,0.3)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 0 #1e3a8a, 0 6px 10px rgba(0,0,0,0.3)'; }}
                data-testid="download-pdf-btn"
              >
                <Download size={16} />
                Download Form (PDF)
              </a>

              <div className="relative">
                <Button 
                  className="bg-[#FF6B00] hover:bg-[#E65C00] text-white px-4 py-2 text-sm font-semibold rounded-lg shadow-lg"
                  onClick={() => setShowFeeOptions(!showFeeOptions)}
                  data-testid="pay-fee-btn"
                >
                  Pay Application Fee ▼
                </Button>
                
                {/* Dropdown Options */}
                {showFeeOptions && (
                  <div className="absolute right-0 bottom-full mb-2 w-48 bg-white rounded-lg shadow-xl z-50 overflow-hidden" data-testid="fee-dropdown">
                    <button 
                      className="w-full px-4 py-3 text-left hover:bg-gray-100 border-b flex justify-between items-center"
                      onClick={() => { window.open('/fees', '_blank'); setShowFeeOptions(false); }}
                    >
                      <span className="text-[#002147] font-medium">Preschool</span>
                      <span className="text-[#FF6B00] font-bold">GH₵ 1,190</span>
                    </button>
                    <button 
                      className="w-full px-4 py-3 text-left hover:bg-gray-100 border-b flex justify-between items-center"
                      onClick={() => { window.open('/fees', '_blank'); setShowFeeOptions(false); }}
                    >
                      <span className="text-[#002147] font-medium">Primary</span>
                      <span className="text-[#FF6B00] font-bold">GH₵ 1,120</span>
                    </button>
                    <button 
                      className="w-full px-4 py-3 text-left hover:bg-gray-100 flex justify-between items-center"
                      onClick={() => { window.open('/fees', '_blank'); setShowFeeOptions(false); }}
                    >
                      <span className="text-[#002147] font-medium">JHS</span>
                      <span className="text-[#FF6B00] font-bold">GH₵ 1,200</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8">
            {isSubmitted ? (
              renderSuccessScreen()
            ) : (
              <>
                {renderStepIndicator()}
                
                <form onSubmit={(e) => { e.preventDefault(); }}>
                  {renderCurrentStep()}
                  
                  <div className="flex justify-between mt-8 pt-6 border-t">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
                      disabled={currentStep === 1 || isSubmitting}
                      data-testid="prev-step-btn"
                    >
                      Previous
                    </Button>
                    
                    {currentStep < totalSteps ? (
                      <Button
                        type="button"
                        className="bg-[#FF6B00] hover:bg-[#E65C00]"
                        onClick={() => setCurrentStep(prev => Math.min(totalSteps, prev + 1))}
                        data-testid="next-step-btn"
                      >
                        Next Step
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        data-testid="submit-application-btn"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="mr-2 h-4 w-4" />
                            Submit Application
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EnrollmentForm;
